import { StateCreator } from "zustand";
import type { CreditModalContext } from "@/components/create/creditModalContext";
import { trackCreditModalShown } from "@/utils/analytics";
import type { CreatePageStore } from "../createPageStore";

export interface UIActions {
  setModifyInput: (show: boolean) => void;
  setModifyPrompt: (prompt: string) => void;
  setIsModifying: (isModifying: boolean) => void;
  setCurrentView: (view: "front" | "back") => void;
  setModificationMode: (isModificationMode: boolean) => void;
  /**
   * `stepIndex` is the step the visitor was ON when the wall was raised.
   * Callers that revert the step first (the 402 handlers) must pass it, or the
   * impression is recorded against the reverted step instead of the one the
   * customer actually hit the wall on.
   */
  setShowCreditModal: (
    context?: NonNullable<CreditModalContext>,
    stepIndex?: number
  ) => void;
  hideCreditModal: () => void;
  setShowCreditCheckout: (config: any) => void;
  hideCreditCheckout: () => void;
  setShowCreditSuccess: (data: {
    credits: number;
    transactionId: string;
  }) => void;
  hideCreditSuccess: () => void;
  // Note: setCreditBalance and setCreditCheckLoading removed - use useCredits() hook
  setCreditModalDismissed: (dismissed: boolean) => void;
  setShowFeedbackModal: () => void;
  hideFeedbackModal: () => void;
  setShowQuoteAccountModal: (show: boolean) => void;
  setCreditPurchaseLoading: (loading: boolean) => void;
  setShowStartOverConfirm: (show: boolean) => void;
}

export const createUISlice: StateCreator<
  CreatePageStore,
  [["zustand/immer", never]],
  [],
  UIActions
> = (set, get) => ({
  // Modification UI actions
  setModifyInput: (show: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modification.showInput = show;
    }),

  setModifyPrompt: (prompt: string) =>
    set((state: CreatePageStore) => {
      state.ui.modification.prompt = prompt;
    }),

  setIsModifying: (isModifying: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modification.isModifying = isModifying;
    }),

  setCurrentView: (view: "front" | "back") =>
    set((state: CreatePageStore) => {
      state.ui.view = view;
    }),

  setModificationMode: (isModificationMode: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modification.isModificationMode = isModificationMode;
    }),

  // Credit modal actions
  //
  // This is the single choke point for the wall appearing. Thirteen call sites
  // open it — generation 402s, modification 402s, the chat-turn limit, the
  // anonymous-unlock wall, preset dispatch — and instrumenting them
  // individually has already failed once: only one ever tracked, and it was
  // the branch signed-out visitors never reach. Tracking here cannot drift as
  // openers are added, and the context each one already passes is the reason
  // the wall is up.
  setShowCreditModal: (context, stepIndex) => {
    const previous = get().ui.modals.credit;
    const nextContext = context ?? null;
    // A wall already on screen for the same reason is not a new impression;
    // a different reason is, even without an intervening close.
    const isNewImpression = !previous.show || previous.context !== nextContext;

    set((state: CreatePageStore) => {
      state.ui.modals.credit.show = true;
      state.ui.modals.credit.context = nextContext;
      state.ui.modals.credit.dismissed = false;
    });

    if (isNewImpression) {
      trackCreditModalShown({
        // The 402 handlers revert the step before raising the wall, so the
        // live step is already 1 or 3 by now. They pass the step the customer
        // was actually on; without it this would report a near-constant that
        // mislabels where the funnel breaks.
        step_index: stepIndex ?? get().form.currentStep,
        context: nextContext,
      });
    }
  },

  hideCreditModal: () =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.show = false;
      state.ui.modals.credit.context = null;
      state.ui.modals.credit.dismissed = true;
    }),

  setShowCreditCheckout: config =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.show = false;
      state.ui.modals.credit.checkout = true;
      state.ui.modals.credit.config = config;
      state.ui.modals.credit.purchaseLoading = false; // Clear loading when checkout opens
    }),

  hideCreditCheckout: () =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.checkout = false;
    }),

  setShowCreditSuccess: data =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.checkout = false;
      state.ui.modals.credit.success = true;
      state.ui.modals.credit.successData = data;
      state.ui.modals.credit.dismissed = false;
    }),

  hideCreditSuccess: () =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.success = false;
      state.ui.modals.credit.successData = null;
    }),

  // Note: setCreditBalance and setCreditCheckLoading removed - use useCredits() hook

  setCreditModalDismissed: (dismissed: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.dismissed = dismissed;
    }),

  // Feedback modal actions
  setShowFeedbackModal: () =>
    set((state: CreatePageStore) => {
      state.ui.modals.feedback = true;
    }),

  hideFeedbackModal: () =>
    set((state: CreatePageStore) => {
      state.ui.modals.feedback = false;
    }),

  setShowQuoteAccountModal: (show: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modals.quoteAccount = show;
    }),

  setCreditPurchaseLoading: (loading: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modals.credit.purchaseLoading = loading;
    }),

  // Start Over confirmation modal — shared by the header button and any
  // agent-offered "Start over" preset so a destructive reset always confirms.
  setShowStartOverConfirm: (show: boolean) =>
    set((state: CreatePageStore) => {
      state.ui.modals.startOverConfirm = show;
    }),
});
